/**
 * apple-login-v1 — Sign in with Apple for the Capacitor shell.
 *
 * App Store Review Guideline 4.8 requires Sign in with Apple in any app
 * that offers a third-party login; Tayro offers Google, so this is a
 * release blocker for the shell.
 *
 * ── WHY THIS IS NOT THE GOOGLE FLOW ──────────────────────────────────
 * Google blocks OAuth inside embedded WebViews, so `lib/nativeAuth.ts`
 * pushes it out to the SYSTEM browser and returns on `tayro://auth-callback`.
 * Apple has no such restriction and the platform expectation is the
 * NATIVE `ASAuthorizationController` sheet, which returns an identity
 * token IN PROCESS — no browser, no deep link. So the token goes
 * straight to `supabase.auth.signInWithIdToken({ provider: 'apple' })`
 * and `onAuthStateChange` publishes the session exactly as it does for a
 * Google deep-link return. Nothing about `tayro://` handling is touched.
 *
 * ── THE NONCE PAIRING (the classic pitfall) ──────────────────────────
 * There are TWO values and they are NOT interchangeable:
 *
 *   rawNonce                → goes to SUPABASE (`signInWithIdToken.nonce`)
 *   SHA-256(rawNonce), hex  → goes to APPLE (the authorize request)
 *
 * Apple copies whatever it was given into the id_token's `nonce` claim.
 * GoTrue hashes the raw nonce we hand it and compares. Send the hash to
 * Supabase (or the raw value to Apple) and the comparison fails with an
 * opaque "Invalid nonce" — the single most common way this integration
 * ships broken. `signInWithAppleNative` is the only place that pairs
 * them, and `appleAuth.test.ts` pins the pairing in both directions.
 *
 * ⚠️ ONE ASSUMPTION, VERIFY ON THE FIRST DEVICE TEST: we hash the nonce
 * ourselves and hand the plugin the HASH, because
 * `@capacitor-community/apple-sign-in` assigns `options.nonce` to
 * `ASAuthorizationAppleIDRequest.nonce` VERBATIM. If a future plugin
 * version starts hashing internally, this would double-hash and every
 * sign-in would fail on the nonce check. Symptom to look for: the sheet
 * completes, then Supabase answers "Invalid nonce" / 401.
 *
 * ── THE PLUGIN IS OPTIONAL AT RUNTIME ────────────────────────────────
 * The plugin is bound BY jsName through `@capacitor/core`'s
 * `registerPlugin` — the house idiom (`lib/nativePush.ts`,
 * `lib/healthKit.ts`) — so the WEB bundle and the tests never import the
 * package's JS, and this module compiles and ships whether or not the
 * npm dependency is installed. When the native side is absent (web
 * build, a shell built before the pod landed) `signInWithAppleNative`
 * returns 'unavailable' and the CALLER degrades to the system-browser
 * OAuth path, which needs no native code at all.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { apiUrl } from './apiBase';
import { seedProfileDisplayName } from './appleNameSeed';
import { isNativeApp } from './platform';

/** The Objective-C plugin name `@capacitor-community/apple-sign-in` registers. */
export const APPLE_PLUGIN_JS_NAME = 'SignInWithApple';

/**
 * apple-signin-spinner (Build 7 field report) — how long we wait for a bridge
 * call before declaring it wedged.
 *
 * The plugin's `authorize` can fail to settle AT ALL: upstream creates its
 * `ASAuthorizationController` as a local variable on Capacitor's background
 * "bridge" queue and never retains it, so presentation can silently no-op —
 * no sheet, no delegate callback, and the saved call never resolves OR
 * rejects. Awaiting it bare is an infinite spinner (exactly what the founder
 * saw on device, 2026-08-02).
 *
 * 30s is deliberately generous: the sheet normally presents in under two
 * seconds, and the case this guards is "nothing ever appeared". The visible-
 * sheet-but-slow-athlete case (reading first-run consent, typing an Apple ID
 * password) is covered by the LATE-COMPLETION continuation in
 * `signInWithAppleNative` — a timeout never strands a sheet the athlete is
 * still working through.
 */
export const APPLE_SHEET_TIMEOUT_MS = 30_000;

/**
 * The value `withAppleTimeout` actually uses. Production never changes it.
 *
 * It exists so tests can exercise the REAL exported entry point with REAL
 * timers on a short budget. The Build-8 device hang got through a suite of
 * nine passing timeout tests because every one of them drove an internal
 * helper under FAKE timers: with `setTimeout` faked and the clock advanced by
 * hand, the tests could not observe that the shipped path had an unbounded
 * await sitting between the timer firing and the error surfacing. Fake-timer
 * tests of internals prove the arithmetic; only a real-timer test of the
 * entry point proves the guarantee.
 */
let appleTimeoutMs: number = APPLE_SHEET_TIMEOUT_MS;

/** Test-only: shorten the budget so a real-timer test finishes in ms.
 *  Call with no argument to restore the shipped value. */
export function __setAppleTimeoutForTest(ms?: number): void {
  appleTimeoutMs = ms ?? APPLE_SHEET_TIMEOUT_MS;
}

/**
 * ⚖️ B231 (2026-09-23, decision Y, finding 3) — the absolute ceiling on the
 * LATE-COMPLETION continuation. Before this it had none: an `authorize()`
 * call that never settled at all could still sign the athlete in minutes, or
 * hours, later. Generous on purpose — the continuation exists to rescue a
 * sheet that was merely SLOW (first-run consent, a typed Apple ID password),
 * not wedged; the 30 s sheet timeout already covers "nothing appeared".
 * Five minutes is long enough for that athlete, short enough that a
 * forgotten tab can never silently commit a session hours later.
 */
export const APPLE_LATE_COMPLETION_BOUND_MS = 5 * 60_000;

let appleLateCompletionBoundMs: number = APPLE_LATE_COMPLETION_BOUND_MS;

/** Test-only: shorten the late-completion bound. No argument restores it. */
export function __setAppleLateCompletionBoundForTest(ms?: number): void {
  appleLateCompletionBoundMs = ms ?? APPLE_LATE_COMPLETION_BOUND_MS;
}

/**
 * Typed timeout — `instanceof`-matched by `mapOAuthError` so the chip can say
 * what actually happened instead of guessing from message keywords.
 */
export class AppleSheetTimeoutError extends Error {
  constructor() {
    super('Apple sign-in timed out before the native sheet responded.');
    this.name = 'AppleSheetTimeoutError';
  }
}

/**
 * Typed "the native plugin is not in this binary" — raised by the CALLER
 * (AuthContext) when the outcome is 'unavailable' inside the shell. It exists
 * so the chip can state the truth ("this build can't do Apple sign-in")
 * rather than silently rerouting the tap into a browser flow the athlete
 * never asked for.
 */
export class ApplePluginUnavailableError extends Error {
  constructor() {
    super('Apple sign-in native plugin is not available in this build.');
    this.name = 'ApplePluginUnavailableError';
  }
}

/**
 * Race `work` against the wedged-bridge timeout. The underlying promise is
 * NOT cancelled (a native call cannot be) — callers that need late results
 * keep their own reference to `work`. `Promise.race` stays subscribed to
 * `work`, so a late rejection is never an unhandled rejection.
 *
 * ⚖️ B231 (2026-09-23) — `onTimeout`, called synchronously the instant this
 * timer fires, before the race rejects. `work` keeps running after that (see
 * above); a caller that needs to know INSIDE `work` that its own clock has
 * already run out — so it can decline to act rather than land invisibly late
 * — flips a flag here and reads it from within `work`. Optional: the other
 * call sites in this file have nothing that needs to hear it.
 */
function withAppleTimeout<T>(work: Promise<T>, onTimeout?: () => void): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      onTimeout?.();
      reject(new AppleSheetTimeoutError());
    }, appleTimeoutMs);
  });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/** What the caller must do next. */
export type AppleNativeOutcome =
  /** Session established — `onAuthStateChange` has (or is about to) fire. */
  | 'signed-in'
  /** Athlete dismissed the sheet. NOT an error: return quietly, no chip. */
  | 'cancelled'
  /** No native sheet here (web build / plugin not in this bundle). Caller
   *  falls back to the system-browser OAuth path. */
  | 'unavailable';

/** The slice of the plugin's response we use. `email`/`givenName`/
 *  `familyName` are populated ONLY on the athlete's FIRST authorization
 *  for this App ID — every later sign-in returns them null. */
interface AppleAuthorizationResponse {
  identityToken?: string | null;
  authorizationCode?: string | null;
  email?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  user?: string | null;
}

interface AppleSignInPlugin {
  authorize(options: {
    clientId?: string;
    redirectURI?: string;
    scopes?: string;
    /** The HASHED nonce — see the nonce-pairing note above. */
    nonce?: string;
  }): Promise<{ response: AppleAuthorizationResponse }>;
}

interface CapacitorGlobal {
  isPluginAvailable?: (name: string) => boolean;
}

/**
 * `ASAuthorizationError.canceled` = 1001. Capacitor surfaces the native
 * NSError as a message string, so the code arrives embedded in text
 * (`The operation couldn't be completed. (com.apple.AuthenticationServices
 * .AuthorizationError error 1001.)`). Matched by code first, wording
 * second — the wording is localized, the code is not.
 */
export function isAppleCancellation(err: unknown): boolean {
  const message = errorMessage(err);
  if (/\b1001\b/.test(message)) return true;
  return /cancel/i.test(message);
}

/**
 * A missing native implementation. Capacitor's bridge rejects an
 * unimplemented plugin method rather than throwing synchronously, so this
 * is the second half of the availability check (`isPluginAvailable` is the
 * first — it is absent on older bridges).
 */
export function isPluginUnavailableError(err: unknown): boolean {
  const message = errorMessage(err);
  return (
    /not implemented/i.test(message) ||
    /unimplemented/i.test(message) ||
    /does not have an implementation/i.test(message) ||
    /not available/i.test(message)
  );
}

function errorMessage(err: unknown): string {
  if (err === null || err === undefined) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  const maybe = err as { message?: unknown; errorMessage?: unknown };
  if (typeof maybe.message === 'string') return maybe.message;
  if (typeof maybe.errorMessage === 'string') return maybe.errorMessage;
  return String(err);
}

/**
 * A fresh raw nonce: 32 bytes of CSPRNG output, hex. Hex (not base64url)
 * because the value travels through a native bridge, a JWT claim and a
 * Postgres comparison, and hex has no encoding variants to disagree about.
 */
export function generateRawNonce(): string {
  const bytes = new Uint8Array(32);
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c || typeof c.getRandomValues !== 'function') {
    // Never silently fall back to Math.random for a replay guard.
    throw new Error('Secure random unavailable, cannot mint an Apple nonce.');
  }
  c.getRandomValues(bytes);
  return toHex(bytes);
}

/** SHA-256 of `input`, lowercase hex — the value APPLE receives. */
export async function sha256Hex(input: string): Promise<string> {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.subtle) {
    throw new Error('WebCrypto subtle unavailable, cannot hash the Apple nonce.');
  }
  const digest = await c.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return toHex(new Uint8Array(digest));
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

let boundPlugin: AppleSignInPlugin | null = null;

/**
 * The plugin proxy, BOXED. Never `Promise<AppleSignInPlugin | null>`.
 *
 * ⛔ §0.7 — THE RAKE THAT COST BUILDS 7, 8 AND 9. Read before editing.
 *
 * `registerPlugin` returns a `Proxy` whose `get` trap converts EVERY property
 * access into a native method call. `then` is not special to it. So the proxy
 * is a THENABLE, and when an async function resolves with it the runtime
 * performs thenable assimilation: it reads `.then`, finds a function, and
 * calls it with `(resolve, reject)`. The proxy forwards `then` to iOS, iOS
 * answers "not implemented", and — decisively — neither continuation is ever
 * invoked. THE AWAITING PROMISE NEVER SETTLES.
 *
 * Device log, founder's iPhone, 2026-08-03:
 *   Unhandled Promise Rejection: Error:
 *   "SignInWithApple.then()" is not implemented on ios
 *
 * That ONE mechanism produced all three reported symptoms and hid its own
 * cause: `await getApplePlugin()` hung, so `authorize()` was never reached
 * (no sheet); the 30s bound is armed on the NEXT line and so never started
 * (no escape); and the rejection surfaced out-of-band, outside the caller's
 * try/catch (no chip). Nothing from rounds 1 and 2 was wrong — main-thread
 * presentation, the retained controller, the presentation anchor, the
 * timeout, the session guard were all simply downstream of a hang that never
 * reached them.
 *
 * The box is what keeps promise resolution's hands off the proxy: a plain
 * object is not thenable, so `.then` is never read. It is also the reason
 * this is now a TYPE-LEVEL guarantee rather than a comment — the signature
 * makes `return boundPlugin` a compile error, and a compile error is the
 * only form of this warning that survives six review rounds.
 *
 * Same wrapper idiom as `lib/run/liveActivity.ts` (`{ la }`),
 * `lib/health/healthKit.ts` (`{ hk }`) and `lib/keyboard/nativeKeyboard.ts`
 * (`{ kb }`). Unwrap AFTER the await, never before.
 */
type BoundApplePlugin = { plugin: AppleSignInPlugin };

/** Bind the native plugin by jsName. Returns null on web, or when the
 *  bridge says the plugin isn't in this build. The proxy comes back BOXED —
 *  see `BoundApplePlugin`; BOTH return paths below must box it. */
async function getApplePlugin(): Promise<BoundApplePlugin | null> {
  if (!isNativeApp()) return null;
  // §0.7 — the cached path is a SEPARATE return statement and carried the
  // identical defect. A fix applied only to the fresh-registration branch
  // below would still hang every athlete who taps Apple a second time.
  if (boundPlugin) return { plugin: boundPlugin };
  const cap = (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor;
  if (typeof cap?.isPluginAvailable === 'function' && !cap.isPluginAvailable(APPLE_PLUGIN_JS_NAME)) {
    return null;
  }
  try {
    // apple-signin-spinner (review closure 3) — the last suspend point on
    // this path, wrapped for completeness rather than for a known failure:
    // it is a local bundle chunk and it rejects rather than hangs. But
    // "every await between the tap and the sheet has a bound" is a property
    // worth having whole, and a timed-out import surfaces as the retryable
    // chip instead of the spinner this lane exists to kill.
    const { registerPlugin } = await withAppleTimeout(import('@capacitor/core'));
    boundPlugin = registerPlugin<AppleSignInPlugin>(APPLE_PLUGIN_JS_NAME);
    return { plugin: boundPlugin };
  } catch (err) {
    // A wedged import is a REAL failure and must reach the chip. Everything
    // else here (@capacitor/core absent, the bridge refusing) stays on the
    // long-standing degrade-to-unavailable path.
    if (err instanceof AppleSheetTimeoutError) throw err;
    return null;
  }
}

/** Test-only: the module-scope plugin cache has to be resettable between
 *  cases. Mirrors `__resetJoinLaunchUrlForTest` in lib/nativeAuth.ts. */
export function __resetApplePluginForTest(): void {
  boundPlugin = null;
}

/**
 * Apple hands over the athlete's name ONLY on the first authorization for
 * this App ID, and it is NOT in the id_token — so Supabase never sees it
 * and `user_metadata` would stay empty forever. Several surfaces greet the
 * athlete from `user_metadata.full_name` / `.name` (routes/today.tsx,
 * settings, team, races, guardian-home, approvals); without this they would
 * fall back to the local part of an `@privaterelay.appleid.com` address.
 *
 * So: capture it in the one moment it exists. Best-effort — a failure here
 * must never fail a sign-in that already succeeded. Existing values win, so
 * a re-authorization can't blank a name the athlete has since edited.
 */
async function captureAppleName(
  client: SupabaseClient,
  response: AppleAuthorizationResponse,
): Promise<void> {
  // The response's own name, OR the stash a previously FAILED exchange left
  // behind (see stashAppleName — the 2026-08-03 incident, where the one first
  // authorization carried the name and the exchange 400'd before this ran).
  const fromResponse = joinAppleName(response.givenName, response.familyName);
  // The sub-matched stash, kept separate from `full`: B73-b's seed is defined
  // on the STASH (a name this device recorded for this exact Apple ID), not on
  // whatever the current response happened to carry.
  const stashed = readAppleNameStash(response.user ?? null);
  const full = fromResponse || stashed;
  if (!full) return; // No name from Apple and no stash — nothing to capture.
  try {
    const { data } = await client.auth.getUser();
    const meta = (data?.user?.user_metadata ?? {}) as Record<string, unknown>;
    const already =
      (typeof meta.full_name === 'string' && meta.full_name.trim().length > 0) ||
      (typeof meta.name === 'string' && meta.name.trim().length > 0);
    if (!already) {
      await client.auth.updateUser({ data: { full_name: full, name: full } });
    }
    // ⚖️ B73-b — and the field Settings' Name row actually EDITS.
    //
    // B73 taught that row to fall back to `user_metadata`, which fixes every
    // account whose metadata holds a name. The Apple account whose metadata
    // never got one still reads "Not set" for a name sitting on this device.
    // `athlete_context.context_data.Name` is the field the row writes and the
    // B53 audit found NOTHING else writing it — so seed it, once, fill-only,
    // and only in the case the brief names: metadata empty, stash present, sub
    // matched. Runs BEFORE the roster write so a 5xx there cannot block it,
    // and never throws — the outcome is a value, and the stash's lifetime
    // below is decided by `propagateProfileName` exactly as it was.
    await seedProfileDisplayName({
      token: await currentAccessToken(client),
      userId: typeof data?.user?.id === 'string' ? data.user.id : null,
      stashedName: stashed,
      metadataHasName: already,
    });
    // ⚠️ THE SECOND DEFECT (apple-name-capture-defect.md): auth metadata is not
    // what the product renders. `public.users.display_name` was written by the
    // signup trigger from a metadata key nobody sends, and the trigger's
    // ON CONFLICT never re-syncs names — so without this call the roster shows
    // the email local part FOREVER, even after a successful capture above.
    // The server decides whether the row still holds a placeholder; this call
    // can only ever FILL, never overwrite.
    await propagateProfileName(client, full);
    // Only clear the stash once the name has actually landed — a failure above
    // throws past this line, and the stash survives for the next sign-in.
    clearAppleNameStash();
  } catch {
    /* best-effort — the athlete is signed in either way */
  }
}

/** `givenName` + `familyName` → one trimmed string, or ''. */
function joinAppleName(given?: string | null, family?: string | null): string {
  return [given, family]
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .join(' ')
    .trim();
}

/**
 * ⛔ THE STASH — write the name down BEFORE anything that can fail.
 *
 * Apple returns the athlete's name EXACTLY ONCE, on the first authorization,
 * forever. The 2026-08-03 incident burned one: the first tap's response
 * carried the name, the Supabase exchange threw (provider not enabled on the
 * project), and `captureAppleName` — which runs only AFTER a successful
 * exchange — never saw it. The retry was a returning-user authorization:
 * no name, by design, permanently.
 *
 * So the name is persisted the moment the sheet returns it, keyed by Apple's
 * stable `user` id (the token's `sub`), and written through on the next
 * SUCCESSFUL sign-in whenever that happens. localStorage, deliberately: it
 * survives the app being killed mid-sign-in, and this module already runs
 * only inside the shell's one WKWebView.
 */
// Exported for `deviceState.clearOnAccountDeletion` (B190 fix4 residue) only
// — the internal read/write/clear helpers below stay the single writers.
export const APPLE_NAME_STASH_KEY = 'tayro_apple_name_stash_v1';

function stashAppleName(response: AppleAuthorizationResponse): void {
  const full = joinAppleName(response.givenName, response.familyName);
  if (!full) return; // Nothing to protect — not a first authorization.
  try {
    localStorage.setItem(
      APPLE_NAME_STASH_KEY,
      JSON.stringify({ sub: response.user ?? null, full }),
    );
  } catch {
    /* storage unavailable — the in-response path still works */
  }
}

/** The stashed name, if it plausibly belongs to THIS sign-in. A stash with a
 *  recorded sub is returned only for the same sub — a different Apple ID on
 *  the same device must never inherit someone else's name. */
function readAppleNameStash(sub: string | null): string {
  try {
    const raw = localStorage.getItem(APPLE_NAME_STASH_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { sub?: string | null; full?: string };
    if (typeof parsed.full !== 'string' || !parsed.full.trim()) return '';
    if (parsed.sub && sub && parsed.sub !== sub) return '';
    return parsed.full.trim();
  } catch {
    return '';
  }
}

function clearAppleNameStash(): void {
  try {
    localStorage.removeItem(APPLE_NAME_STASH_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Fill `public.users.display_name`/`name` when they still hold the signup
 * placeholder. Server-guarded (POST /api/user/profile-name): the endpoint
 * refuses to touch a row whose display name is anything OTHER than the email
 * local part, so a name the athlete has since edited can never be clobbered
 * from here. Throws on 5xx/network so the caller keeps the stash.
 */
async function propagateProfileName(client: SupabaseClient, full: string): Promise<void> {
  const token = await currentAccessToken(client);
  if (!token) return; // No session — nothing to write to.
  const res = await fetch(apiUrl('/api/user/profile-name'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fullName: full }),
  });
  // 4xx = the server REFUSED (row already carries a real name, bad input) —
  // that is an ANSWER, not a failure; retrying with the same stash would loop.
  // 5xx/network = a failure worth keeping the stash for.
  // ⚠️ 404 rides with the failures, deliberately: it is DEPLOYMENT SKEW (a
  // build carrying this code against a server that has not shipped the
  // endpoint yet), not a refusal — spending the one copy of the name on a
  // version mismatch would be the incident all over again.
  if (res.status >= 500 || res.status === 404) {
    throw new Error(`profile-name write failed: ${res.status}`);
  }
}

/**
 * The current session's access token, or null. Never throws — a read that
 * fails must not decide anything on its own (its caller treats "unknown" as
 * the conservative answer).
 */
async function currentAccessToken(client: SupabaseClient): Promise<string | null> {
  try {
    const { data } = await client.auth.getSession();
    return data?.session?.access_token ?? null;
  } catch {
    return null;
  }
}

/**
 * "Did a DIFFERENT sign-in land while the Apple call was wedged?"
 * (apple-signin-spinner, review closure 1.)
 *
 * Deliberately an IDENTITY comparison, not a clock comparison: the only
 * timestamps available (`last_sign_in_at`, the JWT `iat`) come from the auth
 * server, and comparing them against a local `Date.now()` taken at the
 * timeout instant would decide an account-safety question on clock skew.
 *
 * `before` is the access token observed at the timeout. Any token now that
 * is not that exact string means the session changed underneath us — a
 * Google sign-in, or a second Apple attempt that already succeeded (which is
 * why the retry-after-timeout double-sign needs no separate in-flight flag:
 * it lands here). A silent token REFRESH would also trip this, and that is
 * the right trade: refusing a redundant late sign-in costs nothing, while a
 * wrong one swaps the athlete's account.
 *
 * Fails CLOSED — if the session cannot be read, assume something is there
 * and skip. The athlete is never stranded by that choice: the timeout chip
 * is already on screen telling them to try again.
 */
async function hasNewerSession(
  client: SupabaseClient,
  before: string | null,
): Promise<boolean> {
  try {
    const { data } = await client.auth.getSession();
    const now = data?.session?.access_token ?? null;
    if (now === null) return false; // still signed out — the rescue is safe.
    return now !== before;
  } catch {
    return true;
  }
}

/**
 * ⚖️ B231 (2026-09-23, decision Y) — the guard a caller threads through so
 * this function can decline to sign in rather than land invisibly late.
 *
 *   · `baseline` — the access token as it stood right BEFORE this exchange
 *     attempt began, for `hasNewerSession` below. `undefined` means "capture
 *     it now" (the ordinary, non-late path); the late-completion continuation
 *     passes its own snapshot, taken at the ORIGINAL timeout instant, through
 *     unchanged — preserving `hasNewerSession`'s identity-comparison contract.
 *   · `deadline` — a REAL abort now (finding 2, security review 2026-09-23),
 *     not just a flag. `controller.abort()` is called by the CALLER's own
 *     timer the instant it gives up on this call, which genuinely tears down
 *     the in-flight exchange fetch below rather than merely ignoring its
 *     eventual answer — see `withAppleTimeout`'s `onTimeout` at the call
 *     sites. `undefined` when nothing is timing this particular call (never,
 *     after this fix — both callers now supply one; see `signInWithAppleNative`).
 *   · `supersededByAuthEvent` — finding 3: a live drop, not just the one-time
 *     `hasNewerSession` snapshot. Only the late-completion continuation wires
 *     this (it can run for minutes; the ordinary path's own 30s window is
 *     short enough that a snapshot at entry and one right before commit
 *     already bracket it tightly). Set true the instant a `SIGNED_IN` or
 *     `SIGNED_OUT` is observed after the continuation's baseline was taken.
 *   · `ownAccessToken` — B231 residual (finding 1, 2026-09-23). The late
 *     continuation's `SIGNED_IN`/`SIGNED_OUT` listener is armed long before
 *     this attempt's own token exists, so without this it cannot tell its
 *     OWN eventual commit apart from a different account's — every commit
 *     fires the same event. `exchangeIdentityToken` fills this in the
 *     instant the token is known; the listener then ignores an event whose
 *     session carries it and treats anything else as a real supersession.
 */
interface ExchangeGuard {
  baseline?: Promise<string | null> | string | null;
  deadline?: { expired: boolean; controller: AbortController };
  supersededByAuthEvent?: { current: boolean };
  ownAccessToken?: { current: string | null };
}

/**
 * ⚖️ B231 (2026-09-23, decision Y, finding 2) — THE DIRECT EXCHANGE.
 *
 * `client.auth.signInWithIdToken` cannot be raced honestly: it calls
 * `_saveSession` and fires `SIGNED_IN` from INSIDE its own promise, the
 * instant its own fetch resolves — however late — with no way for a caller
 * to intervene between "the response landed" and "the session was written",
 * and no `AbortSignal` option on its public shape
 * (`@supabase/auth-js` `SignInWithIdTokenCredentials`). `withAppleTimeout`'s
 * `Promise.race` only abandons the WAIT; the call it wrapped keeps running,
 * and if it eventually resolves, still saves. That is finding 2: a stalled
 * request outliving the 30s wait, landing after a Google sign-in, silently
 * replacing it.
 *
 * So this function makes the SAME request `signInWithIdToken` makes —
 * `POST {url}/token?grant_type=id_token`, `{provider, id_token, nonce,
 * gotrue_meta_security}`, `GoTrueClient.js` verbatim (`signInWithIdToken`,
 * `@supabase/auth-js/dist/module/GoTrueClient.js`) — under a REAL
 * `AbortSignal`, and returns the raw tokens rather than committing them.
 * `exchangeIdentityToken` decides whether to commit, re-checking the deadline
 * and `hasNewerSession` one more time immediately before — the gap an abort
 * alone cannot close (the response can still land in the instant between the
 * signal firing and the fetch noticing).
 *
 * ⚠️ `client.auth.url` / `.headers` are `protected` in `@supabase/auth-js`'s
 * own `.d.ts` — not a stable public contract, reached into here only because
 * there is no supported public API for a cancellable id-token exchange (the
 * reviewer's own direction, having read the same vendored source). Runtime-
 * safe today: plain JS instance properties under the TS annotation, not real
 * private fields. `appleAuth.directExchangeShape.test.ts` pins the vendored
 * request/response shape this depends on against the installed package, so a
 * `@supabase/auth-js` upgrade that moves them fails that test loudly instead
 * of this failing silently on a device.
 */
async function exchangeIdTokenDirect(
  client: SupabaseClient,
  params: { identityToken: string; nonce: string; signal: AbortSignal },
): Promise<{ access_token: string; refresh_token: string; user_id: string | null }> {
  const auth = client.auth as unknown as { url?: unknown; headers?: unknown };
  if (typeof auth.url !== 'string' || !auth.url || typeof auth.headers !== 'object' || !auth.headers) {
    throw new Error(
      'Apple sign-in: the Supabase auth client did not expose the url/headers shape the direct exchange needs.',
    );
  }
  let res: Response;
  try {
    res = await fetch(`${auth.url}/token?grant_type=id_token`, {
      method: 'POST',
      headers: { ...(auth.headers as Record<string, string>), 'Content-Type': 'application/json;charset=UTF-8' },
      body: JSON.stringify({
        provider: 'apple',
        id_token: params.identityToken,
        nonce: params.nonce,
        gotrue_meta_security: {},
      }),
      signal: params.signal,
    });
  } catch (err) {
    // An abort surfaces here as a DOMException/AbortError — the caller
    // decides what an aborted exchange means (nothing: the deadline already
    // told the athlete the truth). Any other network failure is a real one.
    throw err;
  }
  const body: unknown = await res.json().catch(() => ({}));
  const b = (body ?? {}) as Record<string, unknown>;
  if (!res.ok) {
    const message =
      (typeof b.msg === 'string' && b.msg) ||
      (typeof b.error_description === 'string' && b.error_description) ||
      (typeof b.error === 'string' && b.error) ||
      `Apple sign-in exchange failed (${res.status})`;
    throw new Error(message);
  }
  const accessToken = b.access_token;
  const refreshToken = b.refresh_token;
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
    throw new Error('Apple sign-in exchange returned no session.');
  }
  // The raw GoTrue token response carries `user` (`_sessionResponse`,
  // `@supabase/auth-js/dist/module/lib/fetch.js`) — the Supabase user id,
  // stable across a token refresh unlike `access_token` itself (harvest
  // 2026-09-23b finding 3: a fast device clock can make `setSession` decide
  // the token is already expired and silently refresh it before saving).
  const userObj = b.user;
  const userId =
    userObj && typeof userObj === 'object' && typeof (userObj as Record<string, unknown>).id === 'string'
      ? ((userObj as Record<string, unknown>).id as string)
      : null;
  return { access_token: accessToken, refresh_token: refreshToken, user_id: userId };
}

/**
 * ⚖️ B231 residual (harvest 2026-09-23b, finding 2) — the self sign-out
 * cannot fail open. `signOut({ scope: 'local' })`'s own result used to be
 * ignored: the installed auth-js's `_signOut` (`GoTrueClient.js` ~1584-1609)
 * returns `{ error }` BEFORE it ever calls `_removeSession()` when
 * `/logout` itself fails with anything other than a 404/401/403 or an
 * already-missing session — a genuine network failure or a 5xx left Apple's
 * session stored exactly where it was.
 *
 * One retry, then fall through to auth-js's own local-only removal path:
 * `_removeSession` (private in `GoTrueClient.d.ts` — not a stable public
 * method, reached into here the same way `exchangeIdTokenDirect` already
 * reaches into `.url`/`.headers`, because there is no supported public API
 * that removes the local session without another round trip to `/logout`).
 * It clears storage directly and fires `SIGNED_OUT` itself
 * (`GoTrueClient.js` ~2079-2089), so it cannot fail the same way twice.
 */
async function forceLocalSignOut(client: SupabaseClient): Promise<void> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { error } = await client.auth.signOut({ scope: 'local' });
      if (!error) return;
    } catch {
      // A thrown network failure ends signed-in exactly like a returned
      // `{ error }` — fall through to the retry, then the local removal.
    }
  }
  const auth = client.auth as unknown as { _removeSession?: () => Promise<void> };
  if (typeof auth._removeSession === 'function') {
    try {
      await auth._removeSession();
    } catch {
      /* nothing left to try — best effort past this point */
    }
  } else {
    // ⚖️ B231 sign-off-2 — every installed `@supabase/auth-js` this module
    // has run against carries `_removeSession`
    // (`appleAuth.removeSessionShape.test.ts` pins it against the real,
    // unmocked class, not the test double). A future version that renamed
    // or dropped it would otherwise land here silently, with `signOut`
    // already having failed twice and nothing left clearing local storage —
    // worth a loud signal instead of a quiet best-effort no-op.
    // eslint-disable-next-line no-console
    console.warn(
      '[auth] forceLocalSignOut: signOut failed twice and _removeSession is not a function on this auth client — the local session may still be stored.',
    );
  }
}

/** Identity-token → Supabase-session exchange, shared by the normal path and
 *  the late-completion continuation so the two can never drift. Throws on a
 *  real failure; name capture stays best-effort inside. Resolves `true` when
 *  this attempt is the one left signed in, `false` whenever it ends signed
 *  out or never committed (B231 residual, finding 3 — the caller must not
 *  report 'signed-in' when this attempt just signed itself back out). */
async function exchangeIdentityToken(
  client: SupabaseClient,
  response: AppleAuthorizationResponse,
  rawNonce: string,
  guard?: ExchangeGuard,
): Promise<boolean> {
  const identityToken = response.identityToken;
  if (!identityToken) {
    throw new Error('Apple sign-in returned no identity token.');
  }
  // ⛔ STASH BEFORE THE EXCHANGE — the ordering IS the fix (2026-08-03). Apple
  // offers the name exactly once; if the exchange throws below (it did:
  // provider not enabled, HTTP 400), everything after it never runs. The one
  // copy of the name that will ever exist must be on disk before we hand
  // control to anything that can fail.
  stashAppleName(response);
  // ⚖️ B231 — the baseline is captured BEFORE `initialize()`, same instant a
  // caller with no guard of its own would have taken it: whatever the session
  // was the moment this attempt committed to running.
  const baseline = guard?.baseline !== undefined ? await guard.baseline : await currentAccessToken(client);
  // Security 4 (harvest 2026-09-22) — WAIT FOR THE CLIENT'S OWN INIT FIRST.
  // `signInWithIdToken` (GoTrueClient.js) neither awaits `initializePromise`
  // nor takes the storage-key lock the way `setSession` does — it goes
  // straight to `_request` + `_saveSession`. Since B147 the AuthContext boot
  // deadline can open this door at 8s while a DIFFERENT account's stored
  // session is still mid refresh on the same key: that refresh's late
  // `_saveSession` can land after ours and overwrite the session we are
  // about to establish, or a failed refresh can delete it. `initialize()` is
  // idempotent (it returns the existing `initializePromise` once one is in
  // flight) and covers exactly that boot-time hydration/refresh, so awaiting
  // it first empties the race. Bounded by the SAME cap this whole exchange
  // already runs under at the tap (`APPLE_SHEET_TIMEOUT_MS` via
  // `withAppleTimeout`, below) — no new timeout, no change to the Google
  // path. `setSession` (below) does this same await itself, so this is now
  // belt-and-suspenders on the commit path and load-bearing only for the
  // deadline/session checks that follow it.
  await client.auth.initialize();
  // ⚖️ B231 — `initialize()` is itself an await this function did not use to
  // have, and it can now run long — exactly the scenario the paragraph above
  // names — so a session can supersede, or this call's own governing
  // timeout can fire, while it does. The check runs again HERE, immediately
  // before the network call: a sign-in whose own 30s limit already fired, or
  // that a newer session superseded while `initialize` ran, never starts the
  // exchange. Silent, not an error — the timeout chip (or nothing, for a
  // superseded late-completion) already told the athlete the truth; this
  // only stops a second, invisible session swap.
  if (guard?.deadline?.expired) return false;
  if (guard?.supersededByAuthEvent?.current) return false;
  if (await hasNewerSession(client, baseline)) return false;

  // ⚖️ B231 (finding 2) — THE DIRECT EXCHANGE, under a real `AbortSignal`
  // bound to the same deadline the checks above already read. A stalled
  // connection is genuinely torn down here, not merely ignored.
  const controller = guard?.deadline?.controller ?? new AbortController();
  let tokens: { access_token: string; refresh_token: string; user_id: string | null };
  try {
    // SUPABASE gets the RAW nonce.
    tokens = await exchangeIdTokenDirect(client, {
      identityToken,
      nonce: rawNonce,
      signal: controller.signal,
    });
  } catch (err) {
    // The deadline beating the response is not a NEW failure to surface —
    // the timeout chip (or nothing, for a superseded late-completion)
    // already told the athlete the truth.
    if (controller.signal.aborted) return false;
    throw err;
  }
  // ⚖️ B231 residual (finding 1) — the late continuation's own SIGNED_IN /
  // SIGNED_OUT listener (armed long before this token existed) can now tell
  // its own eventual commit apart from a different account's; see the
  // `ownAccessToken` field's own comment.
  if (guard?.ownAccessToken) guard.ownAccessToken.current = tokens.access_token;

  // ⚖️ B231 — RE-CHECKED, ONE LAST TIME, IMMEDIATELY BEFORE THE COMMIT. The
  // exchange above can still have been in flight the instant the deadline
  // fired (the response and the abort can race each other at the wire), and
  // the late-completion continuation runs for as long as
  // `supersededByAuthEvent` allows, independent of this one call's duration.
  if (guard?.deadline?.expired) return false;
  if (guard?.supersededByAuthEvent?.current) return false;
  if (await hasNewerSession(client, baseline)) return false;

  // ⚖️ B231 residual (2026-09-23, finding 1) — `setSession` below makes its
  // OWN unabortable GET /user before it saves and fires SIGNED_IN
  // (GoTrueClient internals; no AbortSignal on its public shape). Every
  // check above runs BEFORE this call, so none of them can see a session
  // that lands WHILE this one is stalled. The late continuation already has
  // a live watch for exactly that (`supersededByAuthEvent`, now
  // self-discriminating via `ownAccessToken`); the ORDINARY path has none —
  // this is its own, scoped tightly to the one call that can stall. Ignores
  // our own commit's event, or a perfectly good sign-in would undo itself.
  const ownRace = guard?.supersededByAuthEvent ? null : { current: false };
  const raceSub = ownRace
    ? client.auth.onAuthStateChange((event, session) => {
        if (event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') return;
        if (session?.access_token === tokens.access_token) return; // our own commit
        ownRace.current = true;
      })
    : null;

  // `setSession` is the PUBLIC commit path: it awaits `initializePromise` and
  // takes the storage-key lock itself (the reviewer's own direction) — the
  // same discipline `initialize()` above already bought this call once.
  //
  // ⚖️ harvest 2026-09-23b (finding 3) — `raceSub` is released in a `finally`.
  // `setSession` throwing a NON-auth error (the installed auth-js's
  // `_setSession` rethrows anything `isAuthError` doesn't recognize,
  // `GoTrueClient.js` ~1410-1415) used to skip the `unsubscribe()` on the
  // line right after it, leaking the listener — and the Apple tokens in its
  // closure — for the life of the client.
  let setSessionError: Awaited<ReturnType<typeof client.auth.setSession>>['error'] = null;
  try {
    // `setSession`'s own public shape wants exactly `{ access_token,
    // refresh_token }` — `user_id` is this module's own addition to
    // `tokens`, for the identity check below, and is never sent to the
    // client's own session store.
    ({ error: setSessionError } = await client.auth.setSession({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
    }));
  } finally {
    raceSub?.data.subscription.unsubscribe();
  }
  if (setSessionError) throw setSessionError;

  // ⚖️ B231 residual — `setSession` resolving without error is not the same
  // as OUR commit being the one left standing. This attempt's own deadline
  // already fired, or a different session landed while the call above was
  // stalled: either way, if our token is still what's stored, it must not
  // stay there. Fail to signed out, never to the wrong account.
  if (guard?.deadline?.expired || guard?.supersededByAuthEvent?.current || ownRace?.current) {
    // ⚖️ B231 sign-off-2 — this read can itself fail (a thrown network
    // error, or a returned `{ error }`), and a failed read must not be
    // read as "not ours": the whole reason this branch runs is that OUR
    // commit may still be the one sitting in storage, and an unreadable
    // session cannot rule that out. Fail the same direction the guard
    // above already chose — signed out, never left on an unverified
    // session that might be the wrong account.
    let sessionResult: Awaited<ReturnType<typeof client.auth.getSession>> | null = null;
    try {
      sessionResult = await client.auth.getSession();
    } catch {
      /* treated as "could still be ours" below */
    }
    if (!sessionResult || sessionResult.error) {
      await forceLocalSignOut(client);
      return false;
    }
    const { data } = sessionResult;
    // ⚖️ harvest 2026-09-23b (finding 3) — identity by USER ID, not the
    // access token. `_setSession` decides for itself whether the token it
    // was handed still has time left (`GoTrueClient.js` ~1373-1381,
    // comparing the JWT's own `exp` against `Date.now()`); on a fast device
    // clock it can conclude ours already expired and silently refresh it
    // before saving (~1382-1390) — the stored session is still OURS, just
    // under a token that no longer equals `tokens.access_token`. Falls back
    // to the token only when the exchange response carried no user id.
    const stillOurs = tokens.user_id
      ? data?.session?.user?.id === tokens.user_id
      : data?.session?.access_token === tokens.access_token;
    if (stillOurs) {
      await forceLocalSignOut(client);
    }
    return false;
  }

  await captureAppleName(client, response);
  return true;
}

/**
 * Run the native Apple sheet and exchange the identity token for a
 * Supabase session.
 *
 * Throws only on a REAL failure (Apple returned no token, Supabase
 * rejected it, the bridge call wedged past `APPLE_SHEET_TIMEOUT_MS`).
 * Cancellation and plugin-absence are outcomes, not exceptions, because
 * neither should reach an error chip — the CALLER decides what
 * 'unavailable' means (inside the shell it is now a surfaced error, never
 * a silent browser reroute).
 */
export async function signInWithAppleNative(
  client: SupabaseClient,
): Promise<AppleNativeOutcome> {
  // §0.7 — the plugin arrives BOXED and is unwrapped AFTER the await. Do not
  // "simplify" this into `const plugin = await getApplePlugin()`; that is the
  // Build 7/8/9 hang, verbatim. See `BoundApplePlugin`.
  //
  // The outer bound is the NET, not the fix. The fix is the box: a promise
  // that can never settle is not a slow operation, and no timeout would have
  // found its cause — it would only have repainted an infinite spinner as a
  // plausible "try again" chip and bought the bug another build. But bounding
  // the whole plugin-resolution phase costs one line and covers every await
  // inside `getApplePlugin` — the dynamic import today, anything a later
  // session adds — so a future unbounded pre-arm await degrades to a chip
  // instead of a spinner.
  //
  // DELIBERATELY NOT MOVED TO THE TAP. Anchoring one bound at the tap and
  // running it through `authorize` would collapse two different budgets into
  // one: this phase should answer in milliseconds, whereas the sheet's 30s
  // exists to tolerate an athlete reading first-run consent and typing an
  // Apple ID password. A single tap-anchored wall clock is either too tight
  // for that athlete or too loose to add anything here — and it would put the
  // late-completion continuation's baseline snapshot (taken AT the timeout
  // instant) on a different clock than the sheet it guards. Two bounds, two
  // budgets, one guarantee each.
  const bound = await withAppleTimeout(getApplePlugin());
  if (!bound) return 'unavailable';
  const { plugin } = bound;

  const rawNonce = generateRawNonce();
  const hashedNonce = await sha256Hex(rawNonce);

  // APPLE gets the HASH. Keep our own handle on the bridge promise — the
  // timeout below abandons the WAIT, never the call.
  const authorizePromise = plugin.authorize({ scopes: 'name email', nonce: hashedNonce });

  let response: AppleAuthorizationResponse;
  try {
    const result = await withAppleTimeout(authorizePromise);
    response = result?.response ?? {};
  } catch (err) {
    if (err instanceof AppleSheetTimeoutError) {
      // The wedged-bridge escape (Build 7's infinite spinner). If the call
      // EVER settles after this — say the sheet was up and the athlete was
      // slowly typing an Apple ID password — finish the exchange in the
      // background: `onAuthStateChange` publishes the session and the
      // welcome surface navigates on `user`, so a late success still lands
      // the athlete in the app instead of vanishing.
      //
      // …but ONLY if the athlete hasn't signed in some other way meanwhile
      // (review closure 1). `signInWithIdToken` REPLACES whatever session is
      // current, and after a 30s Apple timeout the natural next move is to
      // tap Google. Apple's private-relay address is very often a DIFFERENT
      // Supabase user than the Google address, so an unguarded late exchange
      // can silently swap an athlete out of the account they just signed
      // into — a data problem, not a UX one. `baseline` is the identity
      // snapshot taken at the timeout instant; see `hasNewerSession`.
      //
      // ⛔ THE RULE THIS LINE-ORDER ENFORCES — read before editing:
      // BETWEEN THE TIMEOUT REJECTION AND `throw err` THERE IS EXACTLY ZERO
      // AWAITING. Not one.
      //
      // Build 8 shipped with `await currentAccessToken(client)` right here,
      // and it hung the device exactly as the un-guarded bridge call did:
      // the 30s timer fired, and the error then sat behind a Supabase call
      // that never returned, so nothing was ever thrown, `signInWithApple`
      // never settled, the welcome surface's `finally` never ran, and the
      // spinner spun forever with no chip. `getSession()` opens with
      // `await this.initializePromise` (GoTrueClient.js:2241) which carries
      // NO timeout of its own — the 5s `lockAcquireTimeout` is acquired
      // after it and does not cover it.
      //
      // The lesson generalises: an escape hatch is only as strong as the
      // weakest await on its path, so the escape path now has none. The
      // baseline read is STARTED here (preserving "snapshot at the timeout
      // instant") and AWAITED inside the background continuation, where a
      // hang costs nothing — it just means no late sign-in, which is the
      // fail-closed direction.
      //
      // ⚖️ B231 — the `hasNewerSession` check used to run right here, before
      // `exchangeIdentityToken`. It now runs INSIDE that function, right
      // before the exchange, on this SAME `baseline` promise passed through
      // unchanged (still "the snapshot at the timeout instant") — the
      // function grew its own `initialize()` await since this comment was
      // written, and a check made before that await no longer guards the call
      // it exists to guard. See `exchangeIdentityToken`'s own B231 note.
      const baseline = currentAccessToken(client);
      // ⚖️ B231 (finding 3, decision Y) — TWO GUARDS A CONTINUATION THAT CAN
      // OUTLIVE ITS OWN CALLER NEEDS AND THE ONE-SHOT PATH DOES NOT:
      //
      //   · an ABSOLUTE bound (`appleLateCompletionBoundMs`) — before this,
      //     an `authorize()` that never settled at all left this `.then()`
      //     armed for the life of the app, ready to sign the athlete in
      //     whenever (if ever) it did.
      //   · a LIVE drop on `SIGNED_IN` or `SIGNED_OUT` — `hasNewerSession`
      //     inside `exchangeIdentityToken` is a snapshot taken once, right
      //     before the exchange; a continuation that can run for minutes
      //     needs to hear an account change the moment it happens, not only
      //     at the one instant it happens to check.
      //
      // Both are wired to the SAME `AbortController` the exchange itself
      // reads (`exchangeIdTokenDirect`), so whichever fires first genuinely
      // tears down an in-flight exchange rather than merely marking it moot.
      const lateController = new AbortController();
      const lateDeadline = { expired: false, controller: lateController };
      const supersededByAuthEvent = { current: false };
      // ⚖️ B231 residual (finding 1, 2026-09-23) — filled in by
      // `exchangeIdentityToken` the instant THIS attempt's own token is
      // known, so this listener (armed here, long before that token exists)
      // can tell its own eventual commit apart from a different account's.
      // Without it, our own SIGNED_IN below would mark itself "superseded".
      const ownAccessToken: { current: string | null } = { current: null };
      const { data: lateAuthSub } = client.auth.onAuthStateChange((event, session) => {
        if (event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') return;
        if (ownAccessToken.current && session?.access_token === ownAccessToken.current) return;
        supersededByAuthEvent.current = true;
        lateController.abort();
      });
      const lateBoundTimer = setTimeout(() => {
        lateDeadline.expired = true;
        lateController.abort();
      }, appleLateCompletionBoundMs);
      void authorizePromise
        .then(async (result) => {
          await exchangeIdentityToken(client, result?.response ?? {}, rawNonce, {
            baseline,
            deadline: lateDeadline,
            supersededByAuthEvent,
            ownAccessToken,
          });
        })
        .catch(() => {
          /* late failure — the chip for the timeout already said "try again" */
        })
        .finally(() => {
          clearTimeout(lateBoundTimer);
          lateAuthSub.subscription.unsubscribe();
        });
      throw err;
    }
    if (isAppleCancellation(err)) return 'cancelled';
    if (isPluginUnavailableError(err)) return 'unavailable';
    throw err;
  }

  // The exchange is a plain network call — the same wedge guard applies
  // (failure mode: the exchange fetch hanging on a dead connection). A late
  // success still lands via onAuthStateChange; name capture is attached to
  // the underlying promise, so it is never skipped by the timeout.
  //
  // ⚖️ B231 (finding 2, decision Y) — `exchangeDeadline.controller` is what
  // makes this a REAL cancellation and not just a flag: `withAppleTimeout`'s
  // `onTimeout` below calls `controller.abort()` the instant it gives up,
  // which genuinely tears down `exchangeIdTokenDirect`'s in-flight fetch
  // (see that function). Before this it merely stopped AWAITING the
  // abandoned `signInWithIdToken` call — the call itself kept running, and a
  // late 200 still saved a session with nothing able to stop it.
  const exchangeController = new AbortController();
  const exchangeDeadline = { expired: false, controller: exchangeController };
  const committed = await withAppleTimeout(
    exchangeIdentityToken(client, response, rawNonce, { deadline: exchangeDeadline }),
    () => {
      exchangeDeadline.expired = true;
      exchangeController.abort();
    },
  );
  // ⚖️ harvest 2026-09-23b (finding 3) — a `false` here means this attempt
  // signed itself back out (a race it lost was discovered only after
  // `setSession` committed; see `exchangeIdentityToken`'s post-commit check).
  // 'cancelled' is the existing outcome that already means "return quietly,
  // no chip" — the athlete was never actually left wrong, and there is
  // nothing here for the caller to surface as an error.
  return committed ? 'signed-in' : 'cancelled';
}
